import { ThemeProvider } from '@emotion/react';
import { Router } from '@reach/router';
import { CheckingDetails } from 'components/checkingDetails/CheckingDetails';
import { GlobalStyles } from 'components/GlobalStyles/GlobalStyles';
import { LoadingScreen } from 'components/loadingScreen/LoadingScreen';
import { GlobalModal } from 'components/modals/GlobalModal';
import { Notifications } from 'components/notifications/Notifications';
import { useCheckSignedIn } from 'helpers/useCheckSignedIn';
import { useNotificationUIUpdates } from 'helpers/useNotificationUIUpdates';
import { useZoho } from 'helpers/useZoho';
import { useGoogleTranslateOnAuth } from 'hooks/useGoogleTranslateOnAuth';
import { DashboardContentPage } from 'pages/dashboard/DashboardContentPage';
import { DashboardInformationPage } from 'pages/dashboard/DashboardInformationPage';
import { ForgottenPassword } from 'pages/forgottenPassword/ForgottenPassword';
import { PublicInformationPage } from 'pages/publicInformationPage/PublicInformationPage';
import { ResetPassword } from 'pages/resetPassword/ResetPassword';
import { AutoLogin } from 'pages/signIn/AutoLogin';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// React DOM Error Fix - MUST be imported first to patch DOM methods
import 'services/reactDOMErrorFix';

// Google Translate Coordinator - Manages translations without blocking
import 'services/googleTranslateCoordinator';

// Google Translate Helper - Ensures translations work after login and language changes  
import 'services/googleTranslateHelper';

// Google Translate Debug - Temporary debugging
import 'services/googleTranslateDebug';

// LocalStorage Debugger - Track selectedLanguage changes
import 'services/localStorageDebugger';

import { Slide, ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { selectAppVisible, selectAuthStatus } from 'reducers/auth';
import { getInitialCryptoPrices } from 'reducers/cryptoPrices';
import { selectisVerified } from 'reducers/verification';
import { initialTheme } from 'theme';
import instance, { ApiResponse } from './api';
import { AppPath, DashboardPath } from './appConstants';
import { endpoints } from './endpoints.config';
import {
    addExpiryDate,
    getItemFromLocalStorage,
    storeInLocalStorage,
    hasContentExpired,
} from './helpers/cache';
import { applyBrandDocumentAttributes } from './helpers/brandIdentity';
import {
    getDevBrandingOverride,
    mergeApiBrandingWithDevOverride,
} from './helpers/devBranding';
import Dashboard from './pages/dashboard/Dashboard';
import { Register } from './pages/register/Register';
import SignIn from './pages/signIn/SignIn';
import { selectImagePath, selectTheme, setBranding } from './reducers/ui';
import { ReviewAmendedOrder } from './pages/crypto/ReviewAmendedOrder';
import { SignificantPartyJumio } from 'pages/SignificantPartyJumio';
import { ManageesJumio } from 'pages/ManageesJumio';
import { useCheckInfoRequest } from './helpers/useCheckInfoRequest';
import { DirectLogin } from 'pages/signIn/DirectLogin';
import { useFetchErrorCodes } from './helpers/useFetchErrorCodes';
import Metadata from 'components/metadata/Metadata';
import { QuickBuyPro } from 'pages/QuickBuyPro/QuickBuyPro';

const themeUrl = endpoints.licenseesmodule.licenseesbranding;

interface Branding {
    companyName: string;
    initialTheme: typeof initialTheme;
    images: { assetsPath: string };
    expiryDate: string;
    bBusinessAccountsEnabled: boolean;
    bPersonalAccountsEnabled: boolean;
}

export const DASHBOARD_MENU_PAGE_SUB_PAGE_PLACEHOLDER = 'home';
export const NOTIFICATIONS_PORTAL_ID = 'notifications-portal';

const applyBranding = (
    dispatch: ReturnType<typeof useDispatch>,
    branding: Branding
) => {
    const contentToCache = addExpiryDate(branding);
    storeInLocalStorage(contentToCache, 'branding');
    localStorage.setItem('companyName', branding.companyName);
    dispatch(
        setBranding({
            theme: {
                colors: {
                    ...branding.initialTheme.colors,
                },
            },
            companyName: branding.companyName,
            imagesPath: branding.images.assetsPath,
            bBusinessAccountsEnabled: branding.bBusinessAccountsEnabled,
            bPersonalAccountsEnabled: branding.bPersonalAccountsEnabled,
        })
    );
};

function App() {
    const dispatch = useDispatch();
    const theme = useSelector(selectTheme);
    const imagesPath = useSelector(selectImagePath);

    const cachedBranding = getItemFromLocalStorage<Branding>('branding');

    useNotificationUIUpdates();
    useZoho();
    useGoogleTranslateOnAuth(); // Ensure Google Translate works after login

    useCheckInfoRequest();
    useFetchErrorCodes();

    // Branding: dev override merges API theme + preset assets; else cache; else API only.
    useEffect(() => {
        const devBranding = getDevBrandingOverride();

        const applyFromApi = (details: Branding) => {
            if (devBranding) {
                applyBranding(
                    dispatch,
                    mergeApiBrandingWithDevOverride(details, devBranding)
                );
            } else {
                applyBranding(dispatch, details);
            }
        };

        if (devBranding) {
            instance
                .get<ApiResponse<Branding>>(themeUrl)
                .then((res) => {
                    if (res.status === 200 && res.data.status === '1') {
                        applyFromApi(res.data.details);
                    } else {
                        applyBranding(dispatch, { ...devBranding, expiryDate: '' });
                    }
                })
                .catch(() => {
                    applyBranding(dispatch, { ...devBranding, expiryDate: '' });
                });
            return;
        }

        if (cachedBranding && !hasContentExpired(cachedBranding)) {
            dispatch(
                setBranding({
                    theme: {
                        colors: { ...cachedBranding.initialTheme.colors },
                    },
                    companyName: cachedBranding.companyName,
                    imagesPath: cachedBranding.images.assetsPath,
                    bBusinessAccountsEnabled: cachedBranding.bBusinessAccountsEnabled,
                    bPersonalAccountsEnabled: cachedBranding.bPersonalAccountsEnabled,
                })
            );
            localStorage.setItem('companyName', cachedBranding.companyName);
            return;
        }

        instance.get<ApiResponse<Branding>>(themeUrl).then((res) => {
            if (res.status === 200 && res.data.status === '1') {
                applyFromApi(res.data.details);
            }
        });
    }, []);

    useEffect(() => {
        applyBrandDocumentAttributes(imagesPath);
    }, [imagesPath]);

    useCheckSignedIn();
    const isAppVisible = useSelector(selectAppVisible);
    const authStatus = useSelector(selectAuthStatus);
    const isVerified = useSelector(selectisVerified);

    useEffect(() => {
        if (authStatus === 'signed_in' && isVerified) {
            dispatch(getInitialCryptoPrices());
        }
    }, [authStatus, isVerified, dispatch]);

    useEffect(() => {
        if (!theme || !document) return;

        const root = document.documentElement;
        root.style.setProperty('--firstColor', theme.colors.first);
        root.style.setProperty('--firstColorHover', theme.colors.firstHover);
        root.style.setProperty('--secondColor', theme.colors.second);
        root.style.setProperty('--secondColorHover', theme.colors.secondHover);
        root.style.setProperty('--thirdColor', theme.colors.third);
        root.style.setProperty('--thirdColorHover', theme.colors.thirdHover);

        root.style.setProperty('--alertGood', theme.colors.alertGood);
        root.style.setProperty('--alertGoodHover', theme.colors.alertGoodHover);
        root.style.setProperty('--alertGoodBackground', theme.colors.alertGoodBackground);
        root.style.setProperty('--alertBad', theme.colors.alertBad);
        root.style.setProperty('--alertBadHover', theme.colors.alertBadHover);
        root.style.setProperty('--alertBadBackground', theme.colors.alertBadBackground);
        root.style.setProperty('--alertNeutral', theme.colors.alertNeutral);
        root.style.setProperty('--alertNeutralHover', theme.colors.alertNeutralHover);
        root.style.setProperty('--alertNeutralBackground', theme.colors.alertNeutralBackground);

        root.style.setProperty('--fifthColor', theme.colors.headingColor);
        root.style.setProperty('--sixthColor', theme.colors.secondBackground);
    }, [theme]);

    if (!isAppVisible) {
        return null;
    }

    return (
        <ThemeProvider theme={theme}>
            <GlobalStyles />
            <div className="Main">
                <GlobalModal />
                <Metadata />
                <LoadingScreen color={theme.colors.first} />
                <Notifications
                    activate={authStatus === 'signed_in'}
                    className="NotificationsContainer"
                    portalID={NOTIFICATIONS_PORTAL_ID}
                />
                <ToastContainer
                    position={toast.POSITION.BOTTOM_CENTER}
                    className="ToastMessage"
                    autoClose={5000}
                    transition={Slide}
                    icon={false}
                    theme={'dark'}
                    hideProgressBar
                    pauseOnHover
                />
                <Router>
                    <SignificantPartyJumio path={AppPath.SIGNIFICANT_PARTY_JUMIO} />
                    <ManageesJumio path={AppPath.MANAGEES_JUMIO} />
                    <DirectLogin path={`${AppPath.DIRECT_LOGIN}`} />
                    <QuickBuyPro path={`${AppPath.QUICK_BUY_PRO}`} />
                    <AutoLogin path={`${AppPath.AUTO_LOGIN}`} />
                    <SignIn path={`:cultureCode${AppPath.SIGN_IN}`} default />
                    <Register path={`:cultureCode${AppPath.REGISTER}`} />
                    <Register
                        path={`:cultureCode${AppPath.WAITLIST_BYPASSED_REGISTRATION}`}
                        skipWaitlist
                    />
                    <ForgottenPassword path={`:cultureCode${AppPath.FORGOTTEN_PASSWORD}`} />
                    <ResetPassword path={`:cultureCode${AppPath.RESET_PASSWORD}`} />
                    {/* We take the user to this route whilst fetching the user information
                    which will include the account type, which will dictate whether we direct
                    to AppPath.PERSONAL or AppPath.BUSINESS dashboard route. */}
                    <CheckingDetails path={`:cultureCode${AppPath.CHECKING_DETAILS}`} />
                    <ReviewAmendedOrder
                        path={`:cultureCode${AppPath.PERSONAL}${AppPath.REVIEW_ORDER}`}
                    />
                    <ReviewAmendedOrder
                        path={`:cultureCode${AppPath.BUSINESS}${AppPath.REVIEW_ORDER}`}
                    />
                    <Dashboard path={`:cultureCode${AppPath.PERSONAL}`}>
                        {/* Info pages are used for CMS content pages such as those linked to in the footer. */}
                        <DashboardInformationPage path={`${DashboardPath.INFO}/:page`} />
                        {/* Some dashboard pages don't have any subpages, so we 
                        include a placeholder for sub-page for those paths so that we can match the 
                        component to this single route. Going forward we could opt to 
                        nest the path instead. */}
                        <DashboardContentPage path={`:page/`} />
                        <DashboardContentPage path={`:page/:sub_page`} />
                        <DashboardContentPage path={`:page/:sub_page/:sub_sub_page`} />
                    </Dashboard>
                    <Dashboard path={`:cultureCode${AppPath.BUSINESS}`}>
                        <DashboardInformationPage path={`${DashboardPath.INFO}/:page`} />
                        <DashboardContentPage path={`:page/`} />
                        <DashboardContentPage path={`:page/:sub_page`} />
                        <DashboardContentPage path={`:page/:sub_page/:sub_sub_page`} />
                    </Dashboard>
                    <PublicInformationPage path={`:cultureCode${AppPath.INFO}/:page`} />
                </Router>
            </div>
        </ThemeProvider>
    );
}

export default App;
